import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import { SDialog } from '@modules/ui-components';
import { DESIGN_PROFILE_OPTIONS } from './BrandStyleKitEditor';
import type {
  AuthorDesignProfile,
  AuthorDesignProfileSet,
} from '@modules/content-plan/services/AuthorPlatformService';

type ApplyBaseColorsDialogProps = {
  open: boolean;
  platformId: string;
  baseProfile: AuthorDesignProfile | null;
  sets: AuthorDesignProfileSet[];
  applying?: boolean;
  onClose: () => void;
  onApply: (targetProfileKeys: string[]) => void | Promise<void>;
};

type TargetRow = {
  profileKey: string;
  label: string;
  currentProfile: AuthorDesignProfile | null;
  state: 'create' | 'update';
  targetReferenceCount: number;
};

export const ApplyBaseColorsDialog: React.FC<ApplyBaseColorsDialogProps> = ({
  open,
  platformId,
  baseProfile,
  sets,
  applying = false,
  onClose,
  onApply,
}) => {
  const rows = useMemo<TargetRow[]>(() => (
    DESIGN_PROFILE_OPTIONS
      .filter((option) => option.value !== 'design')
      .map((option) => {
        const set = sets.find((item) => String(item.profileKey) === option.value);
        const currentId = String(set?.currentProfileId ?? '').trim();
        const currentProfile = set?.profiles?.find((profile) => profile.id === currentId)
          ?? set?.profiles?.[0]
          ?? null;
        return {
          profileKey: option.value,
          label: option.label,
          currentProfile,
          state: currentProfile ? 'update' : 'create',
          targetReferenceCount: currentProfile?.visualReferences?.length ?? 0,
        };
      })
  ), [sets]);
  const [selected, setSelected] = useState<string[]>([]);
  const basePalette = paletteFromProfile(baseProfile);
  const baseReferenceCount = baseProfile?.visualReferences?.length ?? 0;

  useEffect(() => {
    if (open) setSelected(rows.map((row) => row.profileKey));
  }, [open, rows]);

  const toggle = (profileKey: string) => {
    setSelected((prev) => (
      prev.includes(profileKey)
        ? prev.filter((key) => key !== profileKey)
        : [...prev, profileKey]
    ));
  };

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title="Apply base colors to post types"
      maxWidth="lg"
      fullWidth
      data-testid={`platform-${platformId}-apply-base-colors-dialog`}
      primaryAction={{
        label: 'Apply colors',
        onClick: () => onApply(selected),
        loading: applying,
        disabled: selected.length === 0 || !baseProfile || basePalette.colors.length === 0,
        startIcon: <PaletteOutlinedIcon />,
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            This creates missing post-type profiles from the current base design profile, or updates only the palette
            on existing post-type profiles. Existing references, typography, composition, and motifs stay unchanged on updates.
          </Typography>
        </Box>

        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Base palette</Typography>
          <PaletteSwatches colors={basePalette.colors} emptyLabel="No base palette colors set." />
          <Typography variant="caption" color="text.secondary">
            Created profiles will copy {baseReferenceCount} reference{baseReferenceCount === 1 ? '' : 's'} from the base profile.
          </Typography>
        </Stack>

        <Stack spacing={1}>
          {rows.map((row) => {
            const targetPalette = paletteFromProfile(row.currentProfile);
            const checked = selected.includes(row.profileKey);
            return (
              <Stack
                key={row.profileKey}
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                sx={{
                  p: 1.25,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Checkbox
                  checked={checked}
                  onChange={() => toggle(row.profileKey)}
                  disabled={applying}
                  sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                />
                <Stack spacing={0.5} sx={{ minWidth: { md: 220 }, flex: '0 0 auto' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{row.label}</Typography>
                    <Chip
                      size="small"
                      color={row.state === 'create' ? 'warning' : 'default'}
                      label={row.state === 'create' ? 'will be created' : 'will be updated'}
                      variant={row.state === 'create' ? 'outlined' : 'filled'}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {row.state === 'create'
                      ? `will copy ${baseReferenceCount} refs`
                      : `keeps ${row.targetReferenceCount} existing refs`}
                  </Typography>
                </Stack>
                <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">Current palette</Typography>
                  <PaletteSwatches colors={targetPalette.colors} emptyLabel="No current palette." />
                  <Typography variant="caption" color="text.secondary">After apply</Typography>
                  <PaletteSwatches colors={basePalette.colors} emptyLabel="No base palette colors set." />
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </SDialog>
  );
};

function paletteFromProfile(profile: AuthorDesignProfile | null | undefined): { colors: string[] } {
  const palette = (profile?.designSystem as any)?.palette ?? {};
  return {
    colors: [
      ...arrayFromValue(palette.primary),
      ...arrayFromValue(palette.accent),
      ...arrayFromValue(palette.neutral),
    ].filter(Boolean),
  };
}

function arrayFromValue(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
}

function PaletteSwatches({
  colors,
  emptyLabel,
}: {
  colors: string[];
  emptyLabel: string;
}) {
  if (colors.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {colors.map((color, index) => (
        <Chip
          key={`${color}-${index}`}
          size="small"
          variant="outlined"
          label={color}
          icon={(
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: color,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            />
          )}
        />
      ))}
    </Stack>
  );
}
